(() => {
  'use strict';

  // The previous gift variant was deleted from Shopify. Keep gift reconciliation
  // disabled until merchandising assigns a real, active zero-price variant.
  const GIFT_VARIANT_ID = 0;
  const ELIGIBLE_SUBTOTAL_CENTS = 20000000;
  const GIFT_MESSAGE = 'Se agregó gratis por superar $200.000';
  const PREFLIGHT_TIMEOUT_MS = 2200;
  const PRODUCT_REQUEST_TIMEOUT_MS = 1500;
  const CART_MUTATION_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/;
  const CHECKOUT_LINK_PATTERN = /\/checkout(?:\?|$)/;
  const nativeFetch = window.fetch.bind(window);
  const shopRoot = window.Shopify?.routes?.root || '/';

  let reconcilePromise = null;
  let cartMutationQueue = Promise.resolve();
  let pending = false;
  let scheduledTimer = null;
  let checkoutInProgress = false;
  let giftUnavailable = true;
  const checkoutControlStates = new WeakMap();

  const pushCroEvent = (event, detail = {}) => {
    const payload = { event, ...detail };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent('evano:cro-event', {
      detail: payload
    }));
  };

  const checkoutSurface = (control, form) => {
    const context = control?.closest?.('[data-cart-wrapper]') || form;
    return context?.closest?.('#t4s-mini_cart')
      ? 'mini_cart'
      : 'main_cart';
  };

  const checkoutAttempt = (control, form) => ({
    checkout_attempt_id:
      window.crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    checkout_surface: checkoutSurface(control, form),
    checkout_started_at: window.performance.now(),
  });

  const checkoutEventDetail = (attempt, detail = {}) => ({
    checkout_attempt_id: attempt.checkout_attempt_id,
    checkout_surface: attempt.checkout_surface,
    checkout_preflight_duration_ms: Math.max(
      0,
      Math.round(window.performance.now() - attempt.checkout_started_at)
    ),
    ...detail,
  });

  const withTimeout = (promise, timeoutMs, message) =>
    new Promise((resolve, reject) => {
      const timeout = window.setTimeout(
        () => reject(new Error(message)),
        timeoutMs
      );

      promise.then(
        (value) => {
          window.clearTimeout(timeout);
          resolve(value);
        },
        (error) => {
          window.clearTimeout(timeout);
          reject(error);
        }
      );
    });

  const setCheckoutControlBusy = (control, busy) => {
    if (!(control instanceof HTMLElement)) return;

    if (busy) {
      if (!checkoutControlStates.has(control)) {
        checkoutControlStates.set(control, {
          disabled: control.disabled,
          ariaDisabled: control.getAttribute('aria-disabled'),
          html: control.innerHTML,
          value:
            control instanceof HTMLInputElement ? control.value : undefined,
        });
      }

      control.setAttribute('aria-busy', 'true');
      control.setAttribute('aria-disabled', 'true');
      if ('disabled' in control) control.disabled = true;
      if (control instanceof HTMLInputElement) {
        control.value = 'Verificando carrito…';
      } else {
        control.textContent = 'Verificando carrito…';
      }
      return;
    }

    const state = checkoutControlStates.get(control);
    control.removeAttribute('aria-busy');
    if (!state) return;

    if (state.ariaDisabled === null) control.removeAttribute('aria-disabled');
    else control.setAttribute('aria-disabled', state.ariaDisabled);
    if ('disabled' in control) control.disabled = state.disabled;
    if (control instanceof HTMLInputElement) control.value = state.value;
    else control.innerHTML = state.html;
    checkoutControlStates.delete(control);
  };

  const cartUrl = (path) => {
    const base = shopRoot.replace(/\/$/, '');
    return path === 'js' ? `${base}/cart.js` : `${base}/cart/${path}`;
  };

  const queueCartMutation = (request) => {
    const result = cartMutationQueue.then(request, request);
    cartMutationQueue = result.catch(() => {});
    return result;
  };

  const requestJson = async (path, options = {}) => {
    const requestUrl = cartUrl(path);
    const request = () => nativeFetch(requestUrl, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body && !(options.body instanceof FormData)
          ? { 'Content-Type': 'application/json' }
          : {}),
      },
      cache: 'no-store',
      ...options,
    });
    const response = CART_MUTATION_PATTERN.test(requestUrl)
      ? await queueCartMutation(request)
      : await request();

    if (!response.ok) {
      const error = new Error(`Cart request failed (${response.status})`);
      error.status = response.status;
      throw error;
    }

    return response.json();
  };

  const eligibleSubtotal = (cart) =>
    cart.items.reduce((subtotal, item) => {
      if (Number(item.variant_id) === GIFT_VARIANT_ID) return subtotal;
      return subtotal + Number(item.original_line_price ?? item.line_price ?? 0);
    }, 0);

  const giftLines = (cart) =>
    cart.items.filter((item) => Number(item.variant_id) === GIFT_VARIANT_ID);

  const validateCartState = (cart) => {
    if (giftUnavailable) return cart;

    const lines = giftLines(cart);
    const qualifies = eligibleSubtotal(cart) >= ELIGIBLE_SUBTOTAL_CENTS;
    const hasOneGift =
      lines.length === 1 && Number(lines[0]?.quantity) === 1;

    if ((qualifies && !hasOneGift) || (!qualifies && lines.length > 0)) {
      throw new Error('Gift cart state could not be verified.');
    }

    return cart;
  };

  const notifyCartChanged = () => {
    const detail = { source: 'evano-free-gift' };
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
    document.body.dispatchEvent(new CustomEvent('currency:update', { detail }));
  };

  const updateGiftLines = async (lines, keepOne) => {
    const updates = {};

    lines.forEach((line, index) => {
      updates[line.key] = keepOne && index === 0 ? 1 : 0;
    });

    await requestJson('update.js', {
      method: 'POST',
      body: JSON.stringify({ updates }),
    });
  };

  const migrateGiftProperties = async (line) => {
    const properties = { ...(line.properties || {}) };
    delete properties._evano_free_gift;
    properties.Obsequio = GIFT_MESSAGE;

    await requestJson('change.js', {
      method: 'POST',
      body: JSON.stringify({
        id: line.key,
        quantity: 1,
        properties,
      }),
    });
  };

  const reconcile = () => {
    if (giftUnavailable) return Promise.resolve(null);

    if (reconcilePromise) {
      pending = true;
      return reconcilePromise;
    }

    reconcilePromise = (async () => {
      try {
        let cart;
        let propertyMigrationAttempted = false;

        do {
          pending = false;
          cart = await requestJson('js');
          const lines = giftLines(cart);
          const qualifies =
            eligibleSubtotal(cart) >= ELIGIBLE_SUBTOTAL_CENTS;
          let changed = false;

          if (!qualifies && lines.length > 0) {
            await updateGiftLines(lines, false);
            changed = true;
          } else if (qualifies && lines.length === 0) {
            try {
              await requestJson('add.js', {
                method: 'POST',
                body: JSON.stringify({
                  items: [
                    {
                      id: GIFT_VARIANT_ID,
                      quantity: 1,
                      properties: { Obsequio: GIFT_MESSAGE },
                    },
                  ],
                }),
              });
            } catch (error) {
              if (error.status === 422) {
                giftUnavailable = true;
                document.dispatchEvent(new CustomEvent('evano:free-gift-unavailable'));
                return cart;
              }
              throw error;
            }
            changed = true;
          } else if (
            qualifies &&
            (lines.length > 1 || Number(lines[0]?.quantity) !== 1)
          ) {
            await updateGiftLines(lines, true);
            changed = true;
          } else if (
            qualifies &&
            lines.length === 1 &&
            lines[0]?.properties?.Obsequio !== GIFT_MESSAGE &&
            !propertyMigrationAttempted
          ) {
            propertyMigrationAttempted = true;
            await migrateGiftProperties(lines[0]);
            changed = true;
          }

          if (changed) {
            notifyCartChanged();
            pending = true;
          }
        } while (pending);

        return validateCartState(cart);
      } catch (error) {
        console.warn('[EVANO free gift] Cart reconciliation failed.', error);
        throw error;
      } finally {
        reconcilePromise = null;
      }
    })();

    return reconcilePromise;
  };

  const scheduleReconcile = (delay = 700) => {
    if (giftUnavailable) return;
    window.clearTimeout(scheduledTimer);
    scheduledTimer = window.setTimeout(() => {
      reconcile().catch(() => {});
    }, delay);
  };

  const formHasUnsavedChanges = (form) =>
    [...form.elements].some((control) => {
      if (!control.name || control.disabled || ['checkout', 'update'].includes(control.name)) {
        return false;
      }
      if (control instanceof HTMLInputElement) {
        if (['checkbox', 'radio'].includes(control.type)) {
          return control.checked !== control.defaultChecked;
        }
        return control.value !== control.defaultValue;
      }
      if (control instanceof HTMLTextAreaElement) {
        return control.value !== control.defaultValue;
      }
      if (control instanceof HTMLSelectElement) {
        return [...control.options].some(
          (option) => option.selected !== option.defaultSelected
        );
      }
      return false;
    });

  const persistCartForm = async (form) => {
    if (!(form instanceof HTMLFormElement) || !formHasUnsavedChanges(form)) {
      return null;
    }

    const formData = new FormData(form);
    formData.delete('checkout');
    formData.delete('update');

    return requestJson('update.js', {
      method: 'POST',
      body: formData,
    });
  };

  const productUrl = (handle) => {
    const base = shopRoot.replace(/\/$/, '');
    return `${base}/products/${encodeURIComponent(handle)}.js`;
  };

  const fetchProduct = async (handle) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(
      () => controller.abort(),
      PRODUCT_REQUEST_TIMEOUT_MS
    );

    try {
      const response = await nativeFetch(productUrl(handle), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Product request failed (${response.status})`);
      }

      return response.json();
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const unavailableCartLines = async (cart) => {
    const sellableLines = cart.items.filter(
      (item) => Number(item.variant_id) !== GIFT_VARIANT_ID && item.handle
    );
    const handles = [...new Set(sellableLines.map((item) => item.handle))];
    const products = await Promise.all(
      handles.map(async (handle) => [handle, await fetchProduct(handle)])
    );
    const productsByHandle = new Map(products);

    return sellableLines.filter((item) => {
      const product = productsByHandle.get(item.handle);
      const variant = product?.variants?.find(
        (candidate) => Number(candidate.id) === Number(item.variant_id)
      );
      return variant ? variant.available === false : false;
    });
  };

  const checkoutPreflight = async (persistedCart = null) => {
    const cart = persistedCart || (await requestJson('js'));
    const unavailableLines = await unavailableCartLines(cart);
    return { cart, unavailableLines };
  };

  const submitCheckout = (destination, form) => {
    if (!(form instanceof HTMLFormElement)) {
      window.location.assign(destination);
      return;
    }

    let checkoutInput = form.querySelector(
      'input[type="hidden"][name="checkout"][data-evano-checkout-submit]'
    );
    if (!checkoutInput) {
      checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = 'checkout';
      checkoutInput.dataset.evanoCheckoutSubmit = '';
      form.append(checkoutInput);
    }

    HTMLFormElement.prototype.submit.call(form);
  };

  const removeCheckoutNotice = () => {
    document.querySelector('[data-evano-checkout-notice]')?.remove();
  };

  const checkoutNotice = ({ control, title, message, primary, secondary }) => {
    removeCheckoutNotice();

    const notice = document.createElement('section');
    notice.dataset.evanoCheckoutNotice = '';
    notice.setAttribute('role', 'alert');
    notice.setAttribute('aria-live', 'assertive');
    notice.tabIndex = -1;
    notice.style.cssText =
      'margin:0 0 12px;padding:16px;background:#fbf8f4;color:#191918;border-block:1px solid #d9d1c8;font-family:Montserrat,Arial,sans-serif;text-align:left';

    const heading = document.createElement('p');
    heading.style.cssText =
      'margin:0 0 4px;font-size:14px;font-weight:600;line-height:1.4';
    heading.textContent = title;
    notice.append(heading);

    if (message) {
      const copy = document.createElement('p');
      copy.style.cssText =
        'margin:0 0 12px;color:#625e58;font-size:13px;font-weight:400;line-height:1.5;overflow-wrap:anywhere';
      copy.textContent = message;
      notice.append(copy);
    }

    const actions = document.createElement('div');
    actions.style.cssText =
      'display:flex;flex-wrap:wrap;align-items:center;gap:8px 16px';

    if (primary) {
      const primaryButton = document.createElement('button');
      primaryButton.type = 'button';
      primaryButton.textContent = primary.label;
      primaryButton.style.cssText =
        'min-height:44px;padding:12px 16px;border:1px solid #191918;border-radius:2px;background:#191918;color:#fff;font:600 12px/1.25 Montserrat,Arial,sans-serif;letter-spacing:.08em;cursor:pointer';
      primaryButton.addEventListener('click', primary.action);
      actions.append(primaryButton);
    }

    if (secondary) {
      const secondaryButton = document.createElement('button');
      secondaryButton.type = 'button';
      secondaryButton.textContent = secondary.label;
      secondaryButton.style.cssText =
        'min-height:44px;padding:12px 0;border:0;border-bottom:1px solid #191918;background:transparent;color:#191918;font:500 14px/1.4 Montserrat,Arial,sans-serif;cursor:pointer';
      secondaryButton.addEventListener('click', secondary.action);
      actions.append(secondaryButton);
    }

    notice.append(actions);
    control?.parentElement?.insertBefore(notice, control);
    if (!notice.isConnected) document.body.append(notice);
    notice.focus({ preventScroll: true });
    return notice;
  };

  const removeUnavailableAndCheckout = async ({
    control,
    destination,
    form,
    unavailableLines,
    attempt,
  }) => {
    checkoutInProgress = true;
    setCheckoutControlBusy(control, true);

    try {
      const updates = Object.fromEntries(
        unavailableLines.map((line) => [line.key, 0])
      );
      const cart = await requestJson('update.js', {
        method: 'POST',
        body: JSON.stringify({ updates }),
      });

      pushCroEvent('checkout_inventory_removed', checkoutEventDetail(attempt, {
        unavailable_item_count: unavailableLines.length,
        checkout_result: 'inventory_removed',
      }));
      notifyCartChanged();

      const remainingItems = cart.items.filter(
        (item) => Number(item.variant_id) !== GIFT_VARIANT_ID
      );
      if (remainingItems.length === 0) {
        checkoutInProgress = false;
        setCheckoutControlBusy(control, false);
        checkoutNotice({
          control,
          title: 'Tu carrito quedó vacío',
          message: 'La prenda agotada fue retirada. Puedes elegir otra disponible.',
          secondary: {
            label: 'Seguir comprando',
            action: () => window.location.assign('/collections/all'),
          },
        });
        return;
      }

      try {
        await withTimeout(
          reconcile(),
          PREFLIGHT_TIMEOUT_MS,
          'Gift reconciliation timed out.'
        );
      } catch (error) {
        console.warn('[EVANO checkout] Gift reconciliation failed open.', error);
        pushCroEvent(
          'checkout_gift_failed_open',
          checkoutEventDetail(attempt, { checkout_result: 'gift_failed_open' })
        );
      }

      submitCheckout(destination, form);
    } catch (error) {
      console.warn('[EVANO checkout] Unavailable item removal failed.', error);
      checkoutInProgress = false;
      setCheckoutControlBusy(control, false);
      checkoutNotice({
        control,
        title: 'No pudimos actualizar tu carrito',
        message: 'Revisa tu conexión e inténtalo de nuevo.',
        secondary: {
          label: 'Reintentar',
          action: () => {
            removeCheckoutNotice();
            removeUnavailableAndCheckout({
              control,
              destination,
              form,
              unavailableLines,
              attempt,
            });
          },
        },
      });
    }
  };

  const showUnavailableNotice = ({
    control,
    destination,
    form,
    unavailableLines,
    attempt,
  }) => {
    const productCount = new Set(unavailableLines.map((line) => line.product_id))
      .size;
    const productTitles = [
      ...new Set(
        unavailableLines.map(
          (line) => line.product_title || line.title || 'Producto agotado'
        )
      ),
    ];
    const listedProducts = productTitles.slice(0, 2).join(', ');
    const remainingProductCount = Math.max(productTitles.length - 2, 0);
    checkoutInProgress = false;
    setCheckoutControlBusy(control, false);
    pushCroEvent('checkout_inventory_blocked', checkoutEventDetail(attempt, {
      unavailable_item_count: unavailableLines.length,
      unavailable_product_count: productCount,
      checkout_result: 'inventory_blocked',
    }));

    checkoutNotice({
      control,
      title:
        productCount === 1
          ? 'Una prenda se agotó en tu carrito'
          : `${productCount} prendas se agotaron en tu carrito`,
      message: `Agotado: ${listedProducts}${
        remainingProductCount ? ` y ${remainingProductCount} más` : ''
      }. Retíralo antes de pagar para continuar sin interrupciones.`,
      primary: {
        label: 'QUITAR Y CONTINUAR',
        action: () =>
          removeUnavailableAndCheckout({
            control,
            destination,
            form,
            unavailableLines,
            attempt,
          }),
      },
      secondary: {
        label: 'Revisar carrito',
        action: () => window.location.assign('/cart'),
      },
    });
  };

  const continueToCheckout = async (
    event,
    destination = '/checkout',
    form = null,
    control = null
  ) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutInProgress) return;

    checkoutInProgress = true;
    removeCheckoutNotice();
    setCheckoutControlBusy(control, true);
    const attempt = checkoutAttempt(control, form);
    pushCroEvent(
      'checkout_preflight_started',
      checkoutEventDetail(attempt, { checkout_result: 'started' })
    );

    try {
      const persistedCart = form ? await persistCartForm(form) : null;
      let preflight = null;

      try {
        preflight = await withTimeout(
          checkoutPreflight(persistedCart),
          PREFLIGHT_TIMEOUT_MS,
          'Checkout preflight timed out.'
        );
      } catch (error) {
        console.warn('[EVANO checkout] Inventory preflight failed open.', error);
        pushCroEvent(
          'checkout_inventory_failed_open',
          checkoutEventDetail(attempt, {
            checkout_result: 'inventory_failed_open',
          })
        );
      }

      if (preflight?.unavailableLines.length) {
        showUnavailableNotice({
          control,
          destination,
          form,
          unavailableLines: preflight.unavailableLines,
          attempt,
        });
        return;
      }

      try {
        await withTimeout(
          reconcile(),
          PREFLIGHT_TIMEOUT_MS,
          'Gift reconciliation timed out.'
        );
      } catch (error) {
        console.warn('[EVANO checkout] Gift reconciliation failed open.', error);
        pushCroEvent(
          'checkout_gift_failed_open',
          checkoutEventDetail(attempt, { checkout_result: 'gift_failed_open' })
        );
      }

      pushCroEvent(
        'checkout_preflight_completed',
        checkoutEventDetail(attempt, {
          cart_item_count: preflight?.cart?.item_count ?? null,
          checkout_result: 'completed',
        })
      );
      submitCheckout(destination, form);
    } catch (error) {
      console.warn('[EVANO checkout] Checkout preparation failed.', error);
      checkoutInProgress = false;
      setCheckoutControlBusy(control, false);
      checkoutNotice({
        control,
        title: 'No pudimos guardar los cambios del carrito',
        message: 'Revisa tu conexión e inténtalo nuevamente.',
        primary: {
          label: 'CONTINUAR AL PAGO',
          action: () => submitCheckout(destination, form),
        },
        secondary: {
          label: 'Reintentar',
          action: () => control?.click(),
        },
      });
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const checkoutControl = event.target.closest?.(
        'a[href], button[name="checkout"], input[name="checkout"]'
      );
      const checkoutLink = checkoutControl?.matches('a[href]')
        ? checkoutControl
        : null;

      if (checkoutLink && CHECKOUT_LINK_PATTERN.test(checkoutLink.href)) {
        continueToCheckout(event, checkoutLink.href, null, checkoutControl);
      } else if (checkoutControl?.name === 'checkout') {
        continueToCheckout(
          event,
          '/checkout',
          checkoutControl.form,
          checkoutControl
        );
      }
    }
  );

  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      const isCheckoutSubmit =
        submitter?.name === 'checkout' || submitter?.value === 'checkout';

      if (isCheckoutSubmit) {
        continueToCheckout(
          event,
          '/checkout',
          event.target,
          event.submitter
        );
      }
    },
    true
  );

  ['cart:refresh', 'cart:updated', 'cart:change', 'ajaxProduct:added'].forEach(
    (eventName) =>
      document.addEventListener(eventName, (event) => {
        if (event.detail?.source === 'evano-free-gift') return;
        scheduleReconcile();
      })
  );

  window.addEventListener('pageshow', () => scheduleReconcile(0));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleReconcile(0), {
      once: true,
    });
  } else {
    scheduleReconcile(0);
  }
})();
